/**
 * Service to manage inactivity settings and connect with admin settings
 */

class InactivitySettingsService {
  constructor() {
    this.cache = new Map();
    this.listeners = new Set();
  }

  /**
   * Get inactivity settings from admin settings
   */
  async getAdminSettings() {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/settings/system`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch admin settings: ${response.status}`);
      }

      const data = await response.json();
      return data.data || {};
    } catch (error) {
      console.warn('Failed to fetch admin settings, using defaults:', error);
      return {
        sessionTimeout: 3600, // 1 hour in seconds
        inactivityWarning: 300, // 5 minutes in seconds
        enableInactivityWarnings: true
      };
    }
  }

  /**
   * Update admin settings with new inactivity configuration
   */
  async updateAdminSettings(settings) {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/settings/system`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionTimeout: settings.sessionTimeout,
          inactivityWarning: settings.inactivityWarning,
          enableInactivityWarnings: settings.enableInactivityWarnings
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update admin settings: ${response.status}`);
      }

      // Notify listeners of the change
      this.notifyListeners(settings);
      return true;
    } catch (error) {
      console.error('Failed to update admin settings:', error);
      throw error;
    }
  }

  /**
   * Get user-specific inactivity preferences
   */
  getUserPreferences() {
    try {
      const stored = localStorage.getItem('inactivityPreferences');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to load user preferences:', error);
      return null;
    }
  }

  /**
   * Save user-specific inactivity preferences
   */
  saveUserPreferences(preferences) {
    try {
      localStorage.setItem('inactivityPreferences', JSON.stringify(preferences));
      this.notifyListeners(preferences);
      return true;
    } catch (error) {
      console.error('Failed to save user preferences:', error);
      return false;
    }
  }

  /**
   * Get effective inactivity configuration
   * Combines admin settings with user preferences
   */
  async getEffectiveConfig(userRole = 'agent') {
    try {
      // Only get admin settings for admin users
      let adminSettings = {};
      if (userRole === 'agentadmin' || userRole === 'admin') {
        adminSettings = await this.getAdminSettings();
      }
      
      // Get user preferences
      const userPreferences = this.getUserPreferences();
      
      // Convert admin settings to milliseconds (use defaults if no admin settings)
      const adminTimeout = (adminSettings.sessionTimeout || 3600) * 1000;
      const adminWarning = (adminSettings.inactivityWarning || 300) * 1000;
      
      // Default configuration
      const defaultConfig = {
        inactivityLimit: adminTimeout,
        modalCountdown: 60,
        warningThresholds: [
          { time: adminWarning, type: 'subtle' },
          { time: adminTimeout - (2 * 60 * 1000), type: 'prominent' }, // 2 minutes before timeout
          { time: adminTimeout, type: 'critical' }
        ],
        enableWarnings: adminSettings.enableInactivityWarnings !== false,
        enableSound: false,
        autoRefresh: true
      };

      // Override with user preferences if available
      if (userPreferences?.timeoutSettings) {
        return {
          ...defaultConfig,
          ...userPreferences.timeoutSettings,
          // Ensure user settings don't exceed admin limits
          inactivityLimit: Math.min(
            userPreferences.timeoutSettings.inactivityLimit || defaultConfig.inactivityLimit,
            adminTimeout
          )
        };
      }

      return defaultConfig;
    } catch (error) {
      console.error('Failed to get effective config:', error);
      // Return safe defaults
      return {
        inactivityLimit: 15 * 60 * 1000, // 15 minutes
        modalCountdown: 60,
        warningThresholds: [
          { time: 10 * 60 * 1000, type: 'subtle' },
          { time: 13 * 60 * 1000, type: 'prominent' },
          { time: 15 * 60 * 1000, type: 'critical' }
        ],
        enableWarnings: true,
        enableSound: false,
        autoRefresh: true
      };
    }
  }

  /**
   * Validate inactivity settings
   */
  validateSettings(settings) {
    const errors = [];

    if (settings.inactivityLimit < 5 * 60 * 1000) {
      errors.push('Inactivity limit must be at least 5 minutes');
    }

    if (settings.inactivityLimit > 2 * 60 * 60 * 1000) {
      errors.push('Inactivity limit cannot exceed 2 hours');
    }

    if (settings.modalCountdown < 10) {
      errors.push('Modal countdown must be at least 10 seconds');
    }

    if (settings.modalCountdown > 300) {
      errors.push('Modal countdown cannot exceed 5 minutes');
    }

    if (settings.warningThresholds) {
      for (const threshold of settings.warningThresholds) {
        if (threshold.time >= settings.inactivityLimit) {
          errors.push('Warning thresholds must be before inactivity limit');
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Add listener for settings changes
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of settings changes
   */
  notifyListeners(settings) {
    this.listeners.forEach(callback => {
      try {
        callback(settings);
      } catch (error) {
        console.error('Error in settings listener:', error);
      }
    });
  }

  /**
   * Reset to default settings
   */
  resetToDefaults() {
    try {
      localStorage.removeItem('inactivityPreferences');
      this.notifyListeners(null);
      return true;
    } catch (error) {
      console.error('Failed to reset settings:', error);
      return false;
    }
  }

  /**
   * Export settings for backup
   */
  exportSettings() {
    try {
      const userPreferences = this.getUserPreferences();
      const data = {
        timestamp: new Date().toISOString(),
        userPreferences,
        version: '1.0'
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inactivity-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Failed to export settings:', error);
      return false;
    }
  }

  /**
   * Import settings from backup
   */
  async importSettings(file) {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (data.userPreferences) {
        this.saveUserPreferences(data.userPreferences);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to import settings:', error);
      throw new Error('Invalid settings file');
    }
  }
}

// Create singleton instance
const inactivitySettingsService = new InactivitySettingsService();

export default inactivitySettingsService;
